import { createFileRoute, Link } from "@tanstack/react-router";
import { ContentPage, ContentSection, BiText } from "@/components/layout/ContentPage";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  head: () => {
    const seo = buildSeo({
      title: "Privacy Policy | Bangla Calculator",
      description:
        "Privacy policy for Bangla Calculator. We don't collect personal data — calculations including Zakat, Faraid, and Prayer Times stay in your browser.",
      path: "/privacy",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Privacy", path: "/privacy" },
      ],
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: PrivacyPage,
});

const LAST_UPDATED = "10 May 2026";

function PrivacyPage() {
  return (
    <ContentPage
      titleEn="Privacy Policy"
      titleBn="গোপনীয়তা নীতি"
      introEn={`Last updated: ${LAST_UPDATED}. Bangla Calculator runs entirely in your browser. We do not collect, store, or sell personal data.`}
      introBn={`সর্বশেষ আপডেট: ${LAST_UPDATED}। বাংলা ক্যালকুলেটর সম্পূর্ণভাবে আপনার ব্রাউজারে চলে। আমরা কোনো ব্যক্তিগত তথ্য সংগ্রহ, সংরক্ষণ বা বিক্রি করি না।`}
    >
      <ContentSection headingEn="1. Information we collect" headingBn="১. আমরা কী তথ্য সংগ্রহ করি">
        <BiText
          en={
            <>
              <p><strong>Personal data:</strong> None. We have no accounts, no signup, no email collection.</p>
              <p><strong>localStorage:</strong> Language preference, theme, calculator history, and saved calculations are stored on YOUR device — not on our servers.</p>
              <p><strong>Geolocation:</strong> Used only client-side for Prayer Times and Qibla Direction. Coordinates never leave your browser.</p>
              <p><strong>Analytics:</strong> Anonymous, aggregate page-view data via a privacy-respecting analytics provider. No personal identifiers.</p>
              <p><strong>Cookies:</strong> Preference cookies only. If/when ads are added in the future, this section will be updated and consent requested.</p>
            </>
          }
          bn={
            <>
              <p><strong>ব্যক্তিগত তথ্য:</strong> কোনোটিই নয়। কোনো অ্যাকাউন্ট, সাইন-আপ বা ইমেইল সংগ্রহ নেই।</p>
              <p><strong>localStorage:</strong> ভাষা পছন্দ, থিম, ক্যালকুলেটর ইতিহাস ও সংরক্ষিত হিসাব আপনার ডিভাইসেই থাকে — আমাদের সার্ভারে নয়।</p>
              <p><strong>অবস্থান (GPS):</strong> শুধু নামাজের সময় ও কিবলা দিকের জন্য ক্লায়েন্ট-সাইডে ব্যবহৃত। কোঅর্ডিনেট কখনো ব্রাউজার ছাড়ে না।</p>
              <p><strong>অ্যানালিটিক্স:</strong> গোপনীয়তা-সম্মান প্রদানকারী প্রোভাইডারের মাধ্যমে অজ্ঞাতনামা পেজ-ভিউ ডাটা। কোনো ব্যক্তিগত শনাক্তকারী নেই।</p>
              <p><strong>কুকি:</strong> শুধু পছন্দ-কুকি। ভবিষ্যতে বিজ্ঞাপন যোগ হলে এই সেকশন আপডেট করে অনুমতি চাওয়া হবে।</p>
            </>
          }
        />
      </ContentSection>

      <ContentSection headingEn="2. Religious privacy" headingBn="২. ধর্মীয় গোপনীয়তা">
        <BiText
          en={
            <p>
              We understand religious calculations are personal. Your <strong>Zakat amount, Faraid distributions, prayer location, and Qibla coordinates</strong> are
              never sent to our servers or any third party. All Islamic calculations happen in your browser. We do not log or share data about which Islamic calculators you use.
            </p>
          }
          bn={
            <p>
              আমরা জানি ধর্মীয় হিসাব ব্যক্তিগত বিষয়। আপনার <strong>যাকাতের পরিমাণ, ফরায়েজ বণ্টন, নামাজের অবস্থান ও কিবলার কোঅর্ডিনেট</strong> কখনোই
              আমাদের সার্ভার বা কোনো তৃতীয় পক্ষের কাছে যায় না। সমস্ত ইসলামিক হিসাব আপনার ব্রাউজারেই হয়। আপনি কোন ইসলামিক ক্যালকুলেটর ব্যবহার করছেন তার কোনো লগ আমরা রাখি না।
            </p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="3. How we use information" headingBn="৩. আমরা তথ্য কীভাবে ব্যবহার করি">
        <ul className="ml-5 list-disc space-y-1.5">
          <BiText
            en={
              <>
                <li>Improve user experience and fix bugs</li>
                <li>Aggregate, anonymous analytics</li>
                <li>No data sold or shared with third parties</li>
              </>
            }
            bn={
              <>
                <li>ব্যবহারকারীর অভিজ্ঞতা উন্নয়ন ও বাগ ফিক্স</li>
                <li>সমষ্টিগত, অজ্ঞাতনামা অ্যানালিটিক্স</li>
                <li>কোনো তথ্য তৃতীয় পক্ষের কাছে বিক্রি বা শেয়ার করা হয় না</li>
              </>
            }
          />
        </ul>
      </ContentSection>

      <ContentSection headingEn="4. Third-party services" headingBn="৪. তৃতীয়-পক্ষের সেবা">
        <ul className="ml-5 list-disc space-y-1.5">
          <BiText
            en={
              <>
                <li>Google Fonts (typography)</li>
                <li>Privacy-respecting analytics (anonymized)</li>
                <li>exchangerate.host — currency rates for the GBP-USD calculator</li>
                <li>Optional gold/silver price API for the Zakat calculator</li>
                <li>Future: Google AdSense (if added) — never on Islamic calculator results pages</li>
              </>
            }
            bn={
              <>
                <li>Google Fonts (টাইপোগ্রাফি)</li>
                <li>গোপনীয়তা-সম্মান প্রদানকারী অ্যানালিটিক্স (অজ্ঞাতনামা)</li>
                <li>exchangerate.host — GBP-USD ক্যালকুলেটরের জন্য কারেন্সি রেট</li>
                <li>যাকাত ক্যালকুলেটরের জন্য ঐচ্ছিক স্বর্ণ/রূপার মূল্য API</li>
                <li>ভবিষ্যৎ: Google AdSense (যোগ হলে) — কোনো ইসলামিক ক্যালকুলেটর ফলাফল পেজে কখনোই নয়</li>
              </>
            }
          />
        </ul>
      </ContentSection>

      <ContentSection headingEn="5. Cookies" headingBn="৫. কুকি">
        <BiText
          en={
            <p>
              Essential preference cookies (language, theme) are required for the site to function. Optional analytics cookies
              are used only with consent. A GDPR-compliant cookie banner is shown to EU visitors.
            </p>
          }
          bn={
            <p>
              অপরিহার্য পছন্দ-কুকি (ভাষা, থিম) সাইট চালানোর জন্য প্রয়োজন। ঐচ্ছিক অ্যানালিটিক্স কুকি শুধু সম্মতিসহ ব্যবহৃত হয়।
              EU ভিজিটরদের জন্য GDPR-সঙ্গতিপূর্ণ কুকি ব্যানার প্রদর্শিত হয়।
            </p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="6. Your rights" headingBn="৬. আপনার অধিকার">
        <ul className="ml-5 list-disc space-y-1.5">
          <BiText
            en={
              <>
                <li>Right to access — we hold nothing personal about you</li>
                <li>Right to delete — clear your browser's localStorage and cookies for our site</li>
                <li>Right to opt out — decline analytics consent at any time</li>
              </>
            }
            bn={
              <>
                <li>প্রবেশাধিকারের অধিকার — আপনার সম্পর্কে আমাদের কাছে কোনো ব্যক্তিগত তথ্য নেই</li>
                <li>মুছে ফেলার অধিকার — আপনার ব্রাউজারে আমাদের সাইটের localStorage ও কুকি মুছুন</li>
                <li>অপ্ট-আউটের অধিকার — যেকোনো সময় অ্যানালিটিক্স সম্মতি প্রত্যাহার</li>
              </>
            }
          />
        </ul>
      </ContentSection>

      <ContentSection headingEn="7. Children's privacy" headingBn="৭. শিশুদের গোপনীয়তা">
        <BiText
          en={<p>This service is not directed at children under 13. We do not knowingly collect data from children.</p>}
          bn={<p>এই সেবা ১৩ বছরের কম বয়সী শিশুদের উদ্দেশ্যে নয়। আমরা ইচ্ছাকৃতভাবে শিশুদের কাছ থেকে তথ্য সংগ্রহ করি না।</p>}
        />
      </ContentSection>

      <ContentSection headingEn="8. Contact" headingBn="৮. যোগাযোগ">
        <BiText
          en={
            <p>
              Privacy questions: <a className="text-accent underline underline-offset-2" href="mailto:banglacalculatorbd@gmail.com">banglacalculatorbd@gmail.com</a>{" "}
              or via <Link to="/contact" className="text-accent underline underline-offset-2">our contact page</Link>.
            </p>
          }
          bn={
            <p>
              গোপনীয়তা সংক্রান্ত প্রশ্ন: <a className="text-accent underline underline-offset-2" href="mailto:banglacalculatorbd@gmail.com">banglacalculatorbd@gmail.com</a>{" "}
              অথবা <Link to="/contact" className="text-accent underline underline-offset-2">যোগাযোগ পেজ</Link>।
            </p>
          }
        />
      </ContentSection>
    </ContentPage>
  );
}
